/**
 * FrostedAirPurifierModal — Apple Home 風格毛玻璃 空氣清淨機 控制面板
 *
 * 設計語言：
 * - 與 FrostedACModal 同系列 frosted-glass 設計
 * - 藍/cyan tint → Air Purifier 專用色調（clean air = blue）
 * - AQI ring dial → PM2.5 數值 + AQI 等級
 * - HEPA filter status card → 濾網壽命百分比 + 剩餘天數
 * - Mode: Auto / Sleep / Turbo / Manual
 * - Fan Speed: Low / Med / High / Auto
 *
 * 完全自包含：parent 只需傳 deviceSN + credentials
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import { executeCommandWithAuth, configureDeviceNicknameWithAuth } from '../services/turingMcpApi'
import { getCurrentApiUrl } from '../services/environmentStore'
import { AirQualityChartStyle01 } from '../components/datacharts/AirQualityChartStyle01'
import './FrostedAirPurifierModal.css'

// ---------------------------------------------------------------------------
// Section definitions (exported for registry)
// ---------------------------------------------------------------------------

export const AIR_PURIFIER_FROSTED_SECTIONS: ModalSectionMeta[] = [
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [] },
  { id: 'dial', label: 'AQI Dial', icon: 'air', slots: [
    { id: 'dial-arc', label: 'Arc Stroke', editableProps: ['strokeColor', 'glow'] },
    { id: 'dial-pm', label: 'PM2.5 Text', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'dial-aqi', label: 'AQI Label', editableProps: ['fontSize', 'color'] },
  ]},
  { id: 'mode', label: 'Mode', icon: 'auto_mode', slots: [
    { id: 'mode-bar', label: 'Mode Bar Container', editableProps: ['background', 'borderRadius', 'border'] },
    { id: 'mode-btn', label: 'Mode Button', editableProps: ['background', 'color', 'borderRadius', 'fontSize'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
  { id: 'fan', label: 'Fan Speed', icon: 'mode_fan', slots: [
    { id: 'fan-label', label: 'Section Label', editableProps: ['fontSize', 'color', 'letterSpacing'] },
    { id: 'fan-btn', label: 'Speed Button', editableProps: ['background', 'color', 'borderRadius', 'border'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
  { id: 'filter', label: 'HEPA Filter', icon: 'filter_alt', slots: [] },
  { id: 'sensors', label: 'Air Quality Sensors', icon: 'sensors', slots: [] },
  { id: 'chart', label: 'PM2.5 History', icon: 'show_chart', slots: [] },
]

const DEFAULT_SECTION_ORDER = AIR_PURIFIER_FROSTED_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MODES = [
  { id: 'auto', label: 'Auto', icon: 'auto_mode' },
  { id: 'sleep', label: 'Sleep', icon: 'dark_mode' },
  { id: 'turbo', label: 'Turbo', icon: 'bolt' },
  { id: 'manual', label: 'Manual', icon: 'tune' },
] as const

const FAN_SPEEDS = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Med' },
  { id: 'high', label: 'High' },
  { id: 'auto', label: 'Auto' },
] as const

// AQI 綜合計算：PM2.5 + CO₂ + TVOC 取最差等級
const AIR_QUALITY_LEVELS = [
  { level: 0, label: '優良', color: 'rgba(74,222,128,0.85)' },
  { level: 1, label: '良好', color: 'rgba(74,222,128,0.85)' },
  { level: 2, label: '普通', color: 'rgba(250,204,21,0.85)' },
  { level: 3, label: '不良', color: 'rgba(251,146,60,0.85)' },
  { level: 4, label: '危險', color: 'rgba(239,68,68,0.85)' },
] as const

function pm25Level(v: number): number {
  if (v <= 15) return 0; if (v <= 35) return 1; if (v <= 54) return 2; if (v <= 150) return 3; return 4
}
function co2Level(v: number): number {
  if (v <= 600) return 0; if (v <= 1000) return 1; if (v <= 1500) return 2; if (v <= 2500) return 3; return 4
}
function tvocLevel(v: number): number {
  if (v <= 65) return 0; if (v <= 220) return 1; if (v <= 660) return 2; if (v <= 2200) return 3; return 4
}

function computeAqi(pm25?: number | null, co2?: number | null, tvoc?: number | null): { label: string; color: string } {
  let worst = 0
  if (pm25 != null) worst = Math.max(worst, pm25Level(pm25))
  if (co2 != null) worst = Math.max(worst, co2Level(co2))
  if (tvoc != null) worst = Math.max(worst, tvocLevel(tvoc))
  return AIR_QUALITY_LEVELS[worst]
}

// ---------------------------------------------------------------------------
// Helpers (same as FrostedACModal)
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}

function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  let v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  if (Array.isArray(v)) v = v[0]
  if (v === undefined || v === null) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

function strAttr(state: Record<string, unknown> | null, ...keys: string[]): string {
  const v = getAttr(state, ...keys)
  return v != null ? String(v) : ''
}

// Ring dial — 270° arc (same geometry as AC modal)
const RING_R = 85
const RING_CX = 100
const RING_CY = 100
const RING_CIRC = 2 * Math.PI * RING_R
const RING_ARC_RATIO = 0.75
const RING_TRACK_OFFSET = RING_CIRC * (1 - RING_ARC_RATIO)

// PM2.5 range: 0–200 μg/m³ for the ring (capped visually)
const PM25_MAX_RING = 200

function pm25ToDashOffset(pm25: number): number {
  const ratio = Math.max(0, Math.min(1, pm25 / PM25_MAX_RING))
  const arcLen = RING_CIRC * RING_ARC_RATIO
  return RING_CIRC - ratio * arcLen
}

const DEG = Math.PI / 180
function pm25ToDotPos(pm25: number): { x: number; y: number } {
  const ratio = Math.max(0, Math.min(1, pm25 / PM25_MAX_RING))
  const angleDeg = 135 + ratio * 270
  const rad = angleDeg * DEG
  return {
    x: RING_CX + RING_R * Math.cos(rad),
    y: RING_CY + RING_R * Math.sin(rad),
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FrostedAirPurifierModal({
  device,
  deviceState,
  onCommand,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
  credentials,
}: DeviceModalTemplateProps) {

  // ---- Context menu for editor mode ----
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  // ---- Section ordering ----
  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- State from device ----
  const isOn = Boolean(getAttr(deviceState, 'on'))
  const _pm25Base = numAttr(deviceState, 'pm25', 'PM2.5', 'particulateMatter25') ?? 0
  const currentMode = strAttr(deviceState, 'currentModeSettings', 'mode') || 'auto'
  const currentFanSpeed = strAttr(deviceState, 'currentFanSpeedSetting', 'fanSpeed') || 'auto'
  // Local sensor state — fetched directly by the modal (self-contained)
  const [sensorData, setSensorData] = useState<{
    co2?: number; tvoc?: number; temperature?: number; humidity?: number; filterLifePercent?: number; pm25?: number
  }>({})

  useEffect(() => {
    if (!credentials || !device.sn) return
    let cancelled = false
    const apiBase = getCurrentApiUrl()
    const hdrs: Record<string, string> = { 'Content-Type': 'application/json' }
    if (credentials.appId) hdrs['Ultron-Cloud-Appid'] = credentials.appId
    if (credentials.apiKey) hdrs['X-Api-Key'] = credentials.apiKey
    else if (credentials.accessToken) hdrs['Authorization'] = `Bearer ${credentials.accessToken}`
    fetch(`${apiBase}/mcp/QueryStatus`, {
      method: 'POST',
      headers: hdrs,
      body: JSON.stringify({ devices: [device.sn] }),
    })
      .then(r => r.json())
      .then(data => {
        if (cancelled) return
        // Debug log removed
        // Parse values from states[].values
        const devArr = (data?.data ?? data?.devices ?? []) as Array<Record<string, unknown>>
        for (const dev of devArr) {
          const states = dev?.states as Array<Record<string, unknown>> | undefined
          if (!Array.isArray(states)) continue
          for (const st of states) {
            if ((st.deviceSN ?? '') !== device.sn && device.sn) continue
            const vals = st.values as Record<string, { value?: unknown }> | undefined
            if (!vals) continue
            const flat: Record<string, unknown> = {}
            for (const [k, v] of Object.entries(vals)) flat[k] = v?.value ?? v
            // Parse currentSensorStateData
            const sd = flat.currentSensorStateData
            if (Array.isArray(sd)) {
              for (const item of sd as Array<{ name?: string; rawValue?: unknown }>) {
                const n = item?.name; const v = item?.rawValue
                if (v == null || n == null) continue
                if (n === 'PM2.5') flat.pm25 = v
                else if (n === 'CarbonDioxideLevel') flat.co2 = v
                else if (n === 'VolatileOrganicCompounds') flat.tvoc = v
                else if (n === 'FilterLifeTime') flat.filterLifePercent = v
              }
            }
            if (flat.FilterLife != null && flat.filterLifePercent == null) flat.filterLifePercent = flat.FilterLife
            const toNum = (v: unknown) => { const n = Number(v); return isNaN(n) ? undefined : n }
            const parsed = {
              co2: toNum(flat.co2),
              tvoc: toNum(flat.tvoc),
              temperature: toNum(flat.temperature),
              humidity: toNum(flat.humidity),
              filterLifePercent: toNum(flat.filterLifePercent),
              pm25: toNum(flat.pm25),
            }
            // Debug log removed
            setSensorData(parsed)
            return
          }
        }
      })
      .catch((err) => { console.error('[AirPurifierModal] sensor fetch failed:', err) })
    return () => { cancelled = true }
  }, [device.sn, credentials])

  const pm25 = sensorData.pm25 ?? _pm25Base
  const filterLife = sensorData.filterLifePercent ?? numAttr(deviceState, 'filterLifePercent', 'filterLife') ?? null
  const co2 = sensorData.co2 ?? numAttr(deviceState, 'co2', 'CarbonDioxideLevel')
  const tvoc = sensorData.tvoc ?? numAttr(deviceState, 'tvoc', 'VolatileOrganicCompounds')
  const temperature = sensorData.temperature ?? numAttr(deviceState, 'temperature')
  const humidity = sensorData.humidity ?? numAttr(deviceState, 'humidity')

  // ---- Settings page ----
  const [showSettings, setShowSettings] = useState(false)
  const deviceInfoItems = useMemo(() => {
    const items: Array<{ label: string; value: string; action?: 'copy' }> = []
    items.push({ label: 'DEVICE TYPE', value: String(device.type || 'Unknown') })
    if (device.ip) items.push({ label: 'IP ADDRESS', value: String(device.ip) })
    if (device.fwVersion || device.firmwareVersion) items.push({ label: 'FW VERSION', value: String(device.fwVersion || device.firmwareVersion) })
    if (device.rssi != null) items.push({ label: 'RSSI', value: String(device.rssi) })
    items.push({ label: 'DEVICE SN', value: device.sn, action: 'copy' })
    return items
  }, [device])
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const copyToClipboard = useCallback((text: string, label: string) => {
    navigator.clipboard.writeText(text).catch(() => {})
    setCopiedField(label)
    setTimeout(() => setCopiedField(null), 1000)
  }, [])

  // ---- Rename ----
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [displayName, setDisplayName] = useState(device.name || device.sn)
  const renameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { setDisplayName(device.name || device.sn) }, [device.name, device.sn])

  const startRename = useCallback(() => {
    setRenameValue(displayName)
    setIsRenaming(true)
    setTimeout(() => renameInputRef.current?.focus(), 50)
  }, [displayName])

  const submitRename = useCallback(async () => {
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === displayName || !credentials) {
      setIsRenaming(false)
      return
    }
    setDisplayName(trimmed)
    setIsRenaming(false)
    await configureDeviceNicknameWithAuth(credentials, device.sn, trimmed).catch(() => {})
  }, [renameValue, displayName, credentials, device.sn])

  // ---- Optimistic UI states ----
  const [pendingMode, setPendingMode] = useState<string | null>(null)
  const [pendingFan, setPendingFan] = useState<string | null>(null)
  const [pendingPower, setPendingPower] = useState<boolean | null>(null)
  const displayMode = pendingMode ?? currentMode
  const displayFan = pendingFan ?? currentFanSpeed
  const displayPower = pendingPower ?? isOn

  useEffect(() => { if (pendingMode !== null && pendingMode === currentMode) setPendingMode(null) }, [currentMode, pendingMode])
  useEffect(() => { if (pendingFan !== null && pendingFan === currentFanSpeed) setPendingFan(null) }, [currentFanSpeed, pendingFan])
  useEffect(() => { if (pendingPower !== null && pendingPower === isOn) setPendingPower(null) }, [isOn, pendingPower])

  // ---- Internal API caller ----
  const execCmd = useCallback((cmd: string, params?: Record<string, unknown>) => {
    if (!credentials || !device.sn) return
    executeCommandWithAuth(credentials, [device.sn], cmd, params).catch(() => {})
    if (onCommand) onCommand(cmd, params)
  }, [credentials, device.sn, onCommand])

  // ---- Commands ----
  const togglePower = useCallback(() => {
    if (isEditorMode) return
    const next = !displayPower
    setPendingPower(next)
    execCmd(next ? 'on' : 'off')
  }, [isEditorMode, displayPower, execCmd])

  const setModeCmd = useCallback((mode: string) => {
    if (isEditorMode) return
    setPendingMode(mode)
    execCmd('setMode', { mode })
  }, [isEditorMode, execCmd])

  const setFanSpeedCmd = useCallback((speed: string) => {
    if (isEditorMode) return
    setPendingFan(speed)
    execCmd('setFanSpeed', { fanSpeed: speed })
  }, [isEditorMode, execCmd])

  // ---- Ring dial values ----
  const displayPM25 = displayPower ? pm25 : 0
  const valueDashOffset = pm25ToDashOffset(displayPM25)
  const dotPos = pm25ToDotPos(displayPM25)
  const aqi = computeAqi(pm25, co2, tvoc)

  // Filter remaining days estimate (rough: filterLife% * 180 days total)
  const filterDaysRemaining = filterLife != null ? Math.round(filterLife * 1.8) : null

  const stateClass = displayPower ? 'ap-frosted--on' : 'ap-frosted--off'
  const hl = (id: string) => highlightedSection === id ? ' section-highlighted' : ''

  // ---- Render sections ----
  const renderSection = (sectionId: string) => {
    if (!isSectionVisible(sectionId)) return null

    switch (sectionId) {
      case 'dial':
        return (
          <div key="dial" className={`ap-frosted__dial${hl('dial')}`} data-modal-section="dial">
            <svg viewBox="0 0 200 200" className="ap-frosted__ring-svg">
              <defs>
                <linearGradient id="apfBlueGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#38bdf8"/>
                  <stop offset="100%" stopColor="#22d3ee"/>
                </linearGradient>
              </defs>
              {/* Track (270° arc) */}
              <circle
                cx={RING_CX} cy={RING_CY} r={RING_R}
                fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={16}
                strokeLinecap="round"
                strokeDasharray={RING_CIRC}
                strokeDashoffset={RING_TRACK_OFFSET}
                transform={`rotate(135,${RING_CX},${RING_CY})`}
              />
              {/* Value arc (blue) */}
              <circle
                cx={RING_CX} cy={RING_CY} r={RING_R}
                fill="none" stroke="url(#apfBlueGrad)" strokeWidth={16}
                strokeLinecap="round"
                strokeDasharray={RING_CIRC}
                strokeDashoffset={displayPower ? valueDashOffset : RING_CIRC}
                transform={`rotate(135,${RING_CX},${RING_CY})`}
                style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                data-modal-slot="dial-arc"
              />
              {/* Dot indicator */}
              {displayPower && (
                <circle
                  cx={dotPos.x} cy={dotPos.y} r={7}
                  fill="#fff"
                  style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))' }}
                />
              )}
            </svg>
            <div className="ap-frosted__dial-text">
              {displayPower ? (
                <>
                  <div data-modal-slot="dial-pm">
                    <span className="ap-frosted__pm-value">{pm25}</span>
                    <span className="ap-frosted__pm-unit">μg/m³</span>
                  </div>
                  <div className="ap-frosted__aqi-label" style={{ '--aqi-color': aqi.color } as React.CSSProperties} data-modal-slot="dial-aqi">
                    <span className="ap-frosted__aqi-title">空氣品質</span>
                    <span className="ap-frosted__aqi-level">{aqi.label}</span>
                  </div>
                </>
              ) : (
                <span className="ap-frosted__off-text">OFF</span>
              )}
            </div>
            {/* Center tap for power toggle */}
            <button
              className="ap-frosted__power-tap"
              onClick={togglePower}
              aria-label={displayPower ? 'Turn off' : 'Turn on'}
            />
          </div>
        )

      case 'mode':
        return (
          <div key="mode" className={`ap-frosted__modes${hl('mode')}`} data-modal-section="mode" data-modal-slot="mode-bar">
            {MODES.map(m => (
              <button
                key={m.id}
                className={`ap-frosted__mode-btn${displayMode === m.id ? ' active' : ''}`}
                onClick={() => setModeCmd(m.id)}
                data-modal-slot="mode-btn"
                data-modal-slot-state={displayMode === m.id ? 'active' : 'default'}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{m.icon}</span> {m.label}
              </button>
            ))}
          </div>
        )

      case 'fan':
        return (
          <div key="fan" className={`ap-frosted__fan${hl('fan')}`} data-modal-section="fan">
            <div className="ap-frosted__fan-label" data-modal-slot="fan-label">Fan Speed</div>
            <div className="ap-frosted__fan-buttons">
              {FAN_SPEEDS.map(f => (
                <button
                  key={f.id}
                  className={`ap-frosted__fan-btn${displayFan === f.id ? ' active' : ''}`}
                  onClick={() => setFanSpeedCmd(f.id)}
                  data-modal-slot="fan-btn"
                  data-modal-slot-state={displayFan === f.id ? 'active' : 'default'}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        )

      case 'filter':
        return (
          <div key="filter" className={`ap-frosted__filter-card${hl('filter')}`} data-modal-section="filter">
            <div className="ap-frosted__filter-header">
              <div className="ap-frosted__filter-title">
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>filter_alt</span>
                HEPA FILTER
              </div>
              <div className="ap-frosted__filter-pct">{filterLife ?? '--'}%</div>
            </div>
            <div className="ap-frosted__filter-bar">
              <div className="ap-frosted__filter-fill" style={{ width: `${filterLife ?? 0}%` }} />
            </div>
            {filterDaysRemaining != null && (
              <div className="ap-frosted__filter-remaining">~{filterDaysRemaining} days remaining</div>
            )}
          </div>
        )

      case 'sensors':
        return (
          <div key="sensors" className={`ap-frosted__sensors-card${hl('sensors')}`} data-modal-section="sensors">
            <div className="ap-frosted__sensor-item">
              <div className="ap-frosted__sensor-top">
                <span className="ap-frosted__sensor-icon material-symbols-outlined">co2</span>
                <span className="ap-frosted__sensor-label">CO₂</span>
              </div>
              <div className="ap-frosted__sensor-bottom">
                <span className="ap-frosted__sensor-value">{co2 ?? '--'}</span>
                {co2 != null && <span className="ap-frosted__sensor-unit">ppm</span>}
              </div>
            </div>
            <div className="ap-frosted__sensor-item">
              <div className="ap-frosted__sensor-top">
                <span className="ap-frosted__sensor-icon material-symbols-outlined">air</span>
                <span className="ap-frosted__sensor-label">TVOC</span>
              </div>
              <div className="ap-frosted__sensor-bottom">
                <span className="ap-frosted__sensor-value">{tvoc ?? '--'}</span>
                {tvoc != null && <span className="ap-frosted__sensor-unit">ppb</span>}
              </div>
            </div>
            <div className="ap-frosted__sensor-item">
              <div className="ap-frosted__sensor-top">
                <span className="ap-frosted__sensor-icon material-symbols-outlined">thermostat</span>
                <span className="ap-frosted__sensor-label">溫度</span>
              </div>
              <div className="ap-frosted__sensor-bottom">
                <span className="ap-frosted__sensor-value">{temperature ?? '--'}</span>
                {temperature != null && <span className="ap-frosted__sensor-unit">°C</span>}
              </div>
            </div>
            <div className="ap-frosted__sensor-item">
              <div className="ap-frosted__sensor-top">
                <span className="ap-frosted__sensor-icon material-symbols-outlined">water_drop</span>
                <span className="ap-frosted__sensor-label">濕度</span>
              </div>
              <div className="ap-frosted__sensor-bottom">
                <span className="ap-frosted__sensor-value">{humidity ?? '--'}</span>
                {humidity != null && <span className="ap-frosted__sensor-unit">%</span>}
              </div>
            </div>
          </div>
        )

      case 'chart': {
        // Use baseSN for chart — sensor data is on the Ultron Sensor endpoint, not #AIRPURIFIER
        const rawSN = device.sn || ''
        const baseSN = rawSN.split('#')[0]
        const chartSN = rawSN.startsWith('MOCK') ? null : (baseSN || null)
        return (
          <div key="chart" className={`ap-frosted__chart-section${hl('chart')}`} data-modal-section="chart">
            <AirQualityChartStyle01
              deviceSN={chartSN}
              credentials={credentials}
            />
          </div>
        )
      }

      default:
        return null
    }
  }

  return (
    <div
      className={`ap-frosted ${stateClass}`}
      data-modal-template-id="air-purifier-frosted"
      onContextMenu={handleContextMenu}
    >
      {cssOverrides && <style>{cssOverrides}</style>}

      {/* Header */}
      <div className="ap-frosted__header" data-modal-section="_modal">
        <button className="ap-frosted__back" onClick={onClose} aria-label="Back">‹</button>
        <span className="ap-frosted__name">{displayName}</span>
        <button
          className={`ap-frosted__power-btn${displayPower ? ' ap-frosted__power-btn--on' : ''}`}
          onClick={togglePower}
          aria-label="Power"
        >
          <span className="material-symbols-outlined" style={{ fontSize: 20 }}>power_settings_new</span>
        </button>
      </div>

      {/* Ordered sections */}
      {orderedSections.map(id => renderSection(id))}

      {/* Settings button */}
      <button
        className="ap-frosted__settings-btn"
        onClick={() => setShowSettings(true)}
        aria-label="Settings"
      >
        <span className="material-symbols-outlined" style={{ fontSize: 28 }}>settings</span>
      </button>

      {/* Settings overlay */}
      {showSettings && (
        <div className="ap-frosted__settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="ap-frosted__settings-sheet" onClick={e => e.stopPropagation()}>
            <div className="ap-frosted__settings-header">
              <span className="ap-frosted__settings-title">設定</span>
              <button className="ap-frosted__settings-close" onClick={() => setShowSettings(false)}>✕</button>
            </div>
            {/* Rename */}
            <div className="ap-frosted__settings-section">
              <div className="ap-frosted__settings-section-title">裝置名稱</div>
              {isRenaming ? (
                <div className="ap-frosted__settings-row">
                  <input
                    ref={renameInputRef}
                    className="ap-frosted__rename-input"
                    value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setIsRenaming(false) }}
                    onBlur={submitRename}
                    maxLength={40}
                  />
                </div>
              ) : (
                <div className="ap-frosted__settings-row">
                  <span className="ap-frosted__settings-value" style={{ textAlign: 'left', flex: 1 }}>
                    {displayName}
                  </span>
                  <button className="ap-frosted__settings-edit" onClick={startRename} title="Rename">
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
                  </button>
                </div>
              )}
            </div>
            {/* Device Info */}
            <div className="ap-frosted__settings-section">
              <div className="ap-frosted__settings-section-title">裝置資訊</div>
              {deviceInfoItems.map(item => (
                <div key={item.label} className="ap-frosted__settings-row">
                  <span className="ap-frosted__settings-label">{item.label}</span>
                  <span className="ap-frosted__settings-value">{item.value}</span>
                  {item.action === 'copy' && (
                    <button
                      className="ap-frosted__settings-copy"
                      onClick={() => copyToClipboard(item.value, item.label)}
                      title="Copy"
                    >
                      📋
                      {copiedField === item.label && (
                        <span className="ap-frosted__copied-toast">Copied</span>
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
